import { useModal } from "@/app/shared/modal-views/use-modal";
import useApi from "@/hooks/useApi";
import { PLACES_CATEGORIES } from "@/utilities/constants/PlacesCategories";
import PlacesRepository from "@/utilities/repositories/Places";
import { IPlace } from "@/utilities/types/Places";
import {
  CreatePlaceInput,
  createPlaceSchema,
} from "@/utils/validators/places.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { PiXBold } from "react-icons/pi";
import { ActionIcon, Button, Input, Title, Select } from "rizzui";

export interface CreateEditPlaceProps {
  refetchData?: () => void;
  data?: IPlace;
}

const CATEGORIES = PLACES_CATEGORIES.sort();

export default function CreateEditPlace({
  refetchData,
  data,
}: CreateEditPlaceProps) {
  const isUpdate = !!data;
  const title = isUpdate ? "Edit Place" : "Create Place";
  const form = useForm({
    defaultValues: {
      name: "",
      category: "",
      latitude: "",
      longitude: "",
    },
    resolver: zodResolver(createPlaceSchema),
  });

  const { closeModal, isOpen } = useModal();

  const { loading, request: createPlace } = useApi({
    request: PlacesRepository.createPlaces,
  });
  const { loading: loadingUpdate, request: updatePlace } = useApi({
    request: PlacesRepository.updatePlaces,
  });

  const onSubmit = async (formData: CreatePlaceInput) => {
    const dataToCreateOrUpdate = {
      ...formData,
      category: formData.category || null,
    };
    try {
      if (isUpdate) {
        await updatePlace(data.id, dataToCreateOrUpdate);
      } else {
        await createPlace(dataToCreateOrUpdate);
      }
      toast.success("Success");
      refetchData?.();
      if (isOpen) {
        closeModal();
      }
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    if (data) {
      form.reset({
        name: data?.name || "",
        category: data?.category || "",
        latitude: data?.latitude || "",
        longitude: data?.longitude || "",
      });
    }
  }, [JSON.stringify(data)]);

  return (
    <form
      noValidate
      className="grid grid-cols-1 gap-6 p-6 @container md:grid-cols-2 [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <div className="col-span-full flex items-center justify-between">
        <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
          {title}
        </Title>
        {isOpen && (
          <ActionIcon size="sm" variant="text" onClick={closeModal}>
            <PiXBold className="h-auto w-5" />
          </ActionIcon>
        )}
      </div>

      <Input
        label="Name"
        size="lg"
        placeholder="Enter place name"
        {...form.register("name")}
        className="[&>label>span]:font-medium col-span-full"
        error={form?.formState?.errors?.name?.message}
      />

      <Select
        label="Category"
        size="lg"
        placeholder="Select category (optional)"
        options={CATEGORIES.map(category => ({ label: category, value: category }))}
        value={form.watch("category") ? { label: form.watch("category"), value: form.watch("category") } : null}
        onChange={(selectedOption: any) => {
          form.setValue("category", selectedOption?.value || "", {
            shouldDirty: true,
          });
        }}
        className="[&>label>span]:font-medium col-span-full"
        error={form?.formState?.errors?.category?.message}
        searchable
        clearable
      />

      <Input
        label="Latitude"
        size="lg"
        placeholder="Enter latitude (-90 to 90)"
        {...form.register("latitude")}
        className="[&>label>span]:font-medium"
        error={form?.formState?.errors?.latitude?.message}
      />

      <Input
        label="Longitude"
        size="lg"
        placeholder="Enter longitude (-180 to 180)"
        {...form.register("longitude")}
        className="[&>label>span]:font-medium"
        error={form?.formState?.errors?.longitude?.message}
      />

      <div className="col-span-full flex items-center justify-end gap-4">
        <Button
          variant="outline"
          onClick={closeModal}
          className="md:w-auto w-1/2"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={loading || loadingUpdate}
          className="md:w-auto w-1/2"
          disabled={!form?.formState?.isDirty}
        >
          {title}
        </Button>
      </div>
    </form>
  );
}
